import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { conn } from '../net/connection';
import type { LoginField } from '../net/protocol';
import { toast, useStore } from '../store';
import { escCloses } from './keys';

/** How long typing settles before it's set in the machine's field. */
const FILL_MS = 150;
/** Fields drawn smaller than this (a scaled-down desktop) get the card instead. */
const MIN_FIELD_PX = 18;
const CARD_W = 240;
const CARD_H = 150;

const autocomplete = (k: LoginField['kind']) => (k === 'username' ? 'username' : k === 'new-password' ? 'new-password' : 'current-password');

const fill = (target: string, field: string, value: string, submit = false) => conn.control('desktop.fill', { target, field, value, submit });

/**
 * A real login form drawn over the one focused in the machine's browser (spec "Links and
 * localhost", the password overlay), so this computer's password manager (the browser's, an
 * extension, the phone's) can fill it. What lands in it is set in the machine's fields
 * (agent loginfields.go), and submitting it submits theirs. It goes away with the form there
 * (the next page, a click elsewhere), or with ✕ until another form is focused.
 */
export function LoginOverlay({ active, frame }: { active: boolean; frame: HTMLIFrameElement | null }) {
  const lf = useStore((s) => s.loginFields);
  const view = useStore((s) => s.desktopView);
  const [closed, setClosed] = useState('');
  const pending = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const key = lf?.fields ? `${lf.target}:${lf.fields.map((f) => f.id).join()}` : '';
  const formRef = useRef<HTMLFormElement | null>(null);
  const inDesktop = useInDesktop(frame, formRef);
  // Only while the person is in the desktop: an agent signing in there while they watch (or
  // work elsewhere) types into the machine's fields, which an overlay would hide.
  const shown = active && inDesktop && !!lf?.fields && !!view && lf.screen[0] > 0 && key !== closed;

  // The overlay's field takes the keyboard from the stream (and brings the password manager's suggestions).
  const focusRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (shown && document.activeElement === frame) focusRef.current?.focus();
  }, [shown, key, frame]);

  if (!shown || !lf?.fields || !view) return null;
  const { target, site, focused, fields } = lf;
  const scale = view.w / lf.screen[0];
  const box = (f: LoginField) => ({ left: view.x + f.x * scale, top: view.y + f.y * scale, width: f.w * scale, height: f.h * scale });
  // On the machine's fields when they show big enough to use; else a card at the focused one.
  const over = fields.every((f) => f.h * scale >= MIN_FIELD_PX);
  const at = box(fields.find((f) => f.id === focused) ?? fields[0]);

  const onInput = (id: string, value: string) => {
    clearTimeout(pending.current.get(id));
    pending.current.set(
      id,
      setTimeout(() => {
        pending.current.delete(id);
        fill(target, id, value).catch(() => {}); // the form went away: the overlay goes too
      }, FILL_MS),
    );
  };
  // Enter, or a password manager submitting: every field as it is now, then the machine's form.
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    pending.current.forEach(clearTimeout);
    pending.current.clear();
    const inputs = [...e.currentTarget.querySelectorAll('input')];
    try {
      for (const [i, el] of inputs.entries()) await fill(target, el.dataset.field!, el.value, i === inputs.length - 1);
    } catch (err) {
      toast({ title: `Couldn’t sign in on ${site}`, body: err instanceof Error ? err.message : String(err), kind: 'error' });
    }
  };

  const chip = (
    <div className="login-overlay-chip" style={over ? { left: at.left, top: Math.max(4, at.top - 26) } : undefined}>
      <span aria-hidden="true">🔑</span> {site}
      <button type="button" className="link-btn" aria-label="Close" data-testid="login-overlay-close" onClick={() => setClosed(key)}>
        ✕
      </button>
    </div>
  );
  const inputs = fields.map((f) => (
    <input
      key={f.id}
      ref={f.id === focused ? focusRef : undefined}
      className="login-overlay-field"
      style={over ? { ...box(f), fontSize: Math.max(11, Math.min(16, f.h * scale * 0.5)) } : undefined}
      type={f.kind === 'username' ? 'text' : 'password'}
      name={f.kind === 'username' ? 'username' : 'password'}
      placeholder={over ? undefined : f.kind === 'username' ? 'Username or email' : 'Password'}
      autoComplete={autocomplete(f.kind)}
      data-field={f.id}
      data-testid={`login-overlay-${f.kind}`}
      onInput={(e) => onInput(f.id, e.currentTarget.value)}
    />
  ));
  // The card stays inside the desktop's picture.
  const card = { left: Math.max(view.x + 8, Math.min(at.left, view.x + view.w - CARD_W - 8)), top: Math.max(view.y + 8, Math.min(at.top + at.height + 6, view.y + view.h - CARD_H)), width: CARD_W };

  return (
    <form
      key={key}
      ref={formRef}
      className="login-overlay"
      onSubmit={(e) => void onSubmit(e)}
      onKeyDown={escCloses(() => (setClosed(key), frame?.focus()))}
      data-testid="login-overlay"
      data-site={site}
      data-layout={over ? 'over' : 'card'}
    >
      {over ? (
        <>
          {chip}
          {inputs}
          <button type="submit" hidden />
        </>
      ) : (
        <div className="login-card" style={card}>
          {chip}
          {inputs}
          <button type="submit" className="btn btn-xs btn-primary" data-testid="login-overlay-submit">
            Sign in
          </button>
        </div>
      )}
    </form>
  );
}

/** The keyboard is in the desktop: its stream frame, or the overlay drawn over it. */
function useInDesktop(frame: HTMLIFrameElement | null, overlay: RefObject<HTMLFormElement | null>): boolean {
  const [inside, setInside] = useState(false);
  useEffect(() => {
    // Focus moving into the frame only blurs this window; activeElement is settled a tick later.
    const check = () => setTimeout(() => setInside(!!frame && (document.activeElement === frame || !!overlay.current?.contains(document.activeElement))));
    check();
    window.addEventListener('blur', check);
    window.addEventListener('focus', check);
    document.addEventListener('focusin', check);
    return () => {
      window.removeEventListener('blur', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('focusin', check);
    };
  }, [frame, overlay]);
  return inside;
}
